import type { BlockerFunction, NavigationType, UIMatch } from 'react-router'
import { Outlet, useLocation, useMatches } from 'react-router'
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'

import { NavigationBlockerProvider } from '../components/NavigationBlockerProvider/NavigationBlockerProvider'
import { parseUrlQuery } from '../../utils/parseUrlQuery'
import { sendPageView, setLocation } from '../utils/googleAnalytics'
import { track } from '../utils/tracking'
import { updateLocation, updateView } from '../store/actions'
import XhrLoadingBar from '../components/XhrLoadingBar'

let isFirstRender = true

export default function RootView() {
  // For scroll restoration
  const scrollYPositions = useRef({})
  const scrollHeights = useRef({})
  const restoreScrollPosition = useCallback((key: string, iteration = 1) => {
    // This only iterates more than once in case of (image) layout jank, which
    // should be avoided. Try up to 5 iterations. Firefox needed up to 5 in
    // test cases, other browsers needed less.
    const maxTries = 5
    if (iteration !== maxTries && document.body.scrollHeight < scrollHeights.current[key]) {
      // We might have layout jank: defer to next event cycle.
      window.setTimeout(() => restoreScrollPosition(key, iteration + 1))
    } else {
      window.scrollTo(0, scrollYPositions.current[key] || 0)
    }
  }, [])

  // Runs when a navigation starts, through the router's only blocker
  // https://github.com/remix-run/react-router/discussions/11374
  // todo take over / recreate patch from https://github.com/ePages-de/ng-merchant-ui/pull/3260 if needed
  const beforeNavigation = useCallback(({ historyAction, currentLocation }: Parameters<BlockerFunction>[0]) => {
    window.getNavigationType = () => historyAction

    // store positions for scroll restoration
    if (currentLocation) {
      scrollHeights.current[currentLocation.key] = document.body.scrollHeight
      scrollYPositions.current[currentLocation.key] = window.scrollY
    }
  }, [])

  const store = useStore<State>()
  const matches = useMatches() as UIMatch<unknown, { Component?: React.ComponentType }>[]

  const location = useLocation()

  // nothing to do upon initial hydration
  useEffect(() => {
    if (isFirstRender) isFirstRender = false
  }, [location, matches, store])

  const dispatch = useDispatch()

  useLayoutEffect(
    function scrollRestorationAndReduxLocation() {
      const action = (typeof window !== 'undefined' && window.getNavigationType?.()) || ('PUSH' as NavigationType)

      // todo this should be removed mid-term, but currently some reducers still use the Redux location
      dispatch(
        updateLocation({
          ...location,
          action,
          query: parseUrlQuery(location.search),
        }),
      )

      dispatch(updateView())

      // Scroll restoration
      // Note: I can't get React Router's <ScrollRestoration /> to work with our current "Data mode",
      // you might retry it when switching to "Framework mode" (Vite plugin). When doing so, make sure that:
      // - { scrollToTop: false } is changed to { preventScrollReset: true }
      // - it still solves the "layout jank" issue (see elsewhere in this file)
      if (isFirstRender) {
        // don't scroll to top if the user has scrolled down while the JS was still loading
      } else if (action === 'PUSH' && location.state?.scrollToTop !== false) {
        window.scrollTo(0, 0)
      } else if (action === 'POP' && !location.hash) {
        window.requestAnimationFrame(() => restoreScrollPosition(location.key))
      } else {
        // eagerly store scroll position reached via { scrollToTop: false },
        // so it can be restored upon browser forward navigation
        scrollYPositions.current[location.key] = window.scrollY
      }

      // Anchor time!
      if (location.hash) {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    [dispatch, location, restoreScrollPosition],
  )

  const isEditor = useSelector<State, boolean>((state) => state.getIn(['view', 'editorMode']))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))

  const trackPageViewTimeoutId = useRef(0)
  const trackPageView = useRef(getPageViewTracker())

  useEffect(
    function tracking() {
      const shouldTrackPageView =
        // don't track when rendered in editor
        !isEditor &&
        // don't track when rendered within an iframe (e.g. theme preview)
        window.top === window.self

      if (isEditor) {
        // tell the MBO/Cockpit where we are (aka. "View your website")
        window.parent.postMessage(
          {
            type: 'PREVIEW_PATH',
            // Note: this discards all query parameters, but that's fine since we don't have any.
            // If we need to pass them along later, we'll have to exclude ['editor', 'shop', 'token'].
            payload: location.pathname.replace(/\/editor(\/themes)?/, '') || '/',
          },
          mboBaseUrl,
        )
      }

      if (shouldTrackPageView) {
        // in order that GA sends the correct pageview field values (e.g. page title),
        // defer the tracking call to wait for the page to be rendered
        if (trackPageViewTimeoutId.current) window.clearTimeout(trackPageViewTimeoutId.current)
        trackPageViewTimeoutId.current = window.setTimeout(() => {
          trackPageView.current(location)
        }, 1)
      }
    },
    [isEditor, location, mboBaseUrl],
  )

  return (
    <NavigationBlockerProvider onBeforeNavigation={beforeNavigation}>
      <Outlet />
      <XhrLoadingBar />
    </NavigationBlockerProvider>
  )
}

export function getPageViewTracker() {
  let lastTrackedPathname = ''
  let lastTrackedSearch = ''

  return function trackPageView({ pathname, search }) {
    if (lastTrackedPathname !== pathname || lastTrackedSearch !== search) {
      lastTrackedPathname = pathname
      lastTrackedSearch = search

      track('page:view', { url: pathname + search })
      setLocation(location)
      sendPageView(pathname)
    }
  }
}
